import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch } from '../../../store';
import {
    fetchBlogDetails,
    createBlogThunk,
    updateBlogThunk,
    clearCurrentBlog,
    useCurrentBlog,
    useBlogLoading,
    useBlogSaving,
    useBlogError,
} from '../../../store/slices/blogSlice';
import BlogImageUploader from './components/BlogImageUploader';
import BlogContentEditor from './components/BlogContentEditor';

interface FormState {
    blogId: string;
    title: string;
    subtitle: string;
    content: string;
    imageUrl: string;
}

interface ValidationErrors {
    image?: string;
    title?: string;
    subtitle?: string;
    content?: string;
}

const EMPTY_FORM: FormState = {
    blogId: '',
    title: '',
    subtitle: '',
    content: '',
    imageUrl: '',
};

export default function CreateBlog() {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { blogId: blogIdParam } = useParams<{ blogId: string }>();

    // ─── 1. Data hooks ───────────────────────────────────────────────────────
    const currentBlog = useCurrentBlog();
    const loading = useBlogLoading();
    const saving = useBlogSaving();
    const apiError = useBlogError();

    // ─── 2. Local state ──────────────────────────────────────────────────────
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

    // ─── 3. Derived values ───────────────────────────────────────────────────
    const isEditMode = useMemo(() => !!blogIdParam, [blogIdParam]);

    const isFormValid = useMemo(() => {
        if (isEditMode) {
            // In edit mode the blogId is the existing _id — image is already present
            return (
                form.title.trim().length > 0 &&
                form.subtitle.trim().length > 0 &&
                form.content.trim().length > 0 &&
                form.imageUrl.trim().length > 0
            );
        }
        // In create mode a new blogId must have been allocated via image upload
        return (
            form.blogId.trim().length > 0 &&
            form.title.trim().length > 0 &&
            form.subtitle.trim().length > 0 &&
            form.content.trim().length > 0 &&
            form.imageUrl.trim().length > 0
        );
    }, [form, isEditMode]);

    // ─── 4. Handlers ─────────────────────────────────────────────────────────
    const handleImageUploadComplete = useCallback(
        (result: { blogId: string; cloudFrontUrl: string }) => {
            setForm((prev) => ({
                ...prev,
                blogId: result.blogId,
                imageUrl: result.cloudFrontUrl,
            }));
            setValidationErrors((prev) => ({ ...prev, image: undefined }));
        },
        []
    );

    const handleTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setForm((prev) => ({ ...prev, title: e.target.value }));
        setValidationErrors((prev) => ({ ...prev, title: undefined }));
    }, []);

    const handleSubtitleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setForm((prev) => ({ ...prev, subtitle: e.target.value }));
        setValidationErrors((prev) => ({ ...prev, subtitle: undefined }));
    }, []);

    const handleContentChange = useCallback((html: string) => {
        setForm((prev) => ({ ...prev, content: html }));
        setValidationErrors((prev) => ({ ...prev, content: undefined }));
    }, []);

    const validate = useCallback((): boolean => {
        const errors: ValidationErrors = {};
        if (!form.imageUrl.trim()) errors.image = 'Cover image is required.';
        if (!form.title.trim()) errors.title = 'Title is required.';
        if (!form.subtitle.trim()) errors.subtitle = 'Subtitle is required.';
        if (!form.content.trim() || form.content === '<p></p>')
            errors.content = 'Content is required.';
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    }, [form]);

    const handleSubmit = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault();
            if (!validate()) return;

            if (isEditMode && blogIdParam) {
                const result = await dispatch(
                    updateBlogThunk({
                        _id: blogIdParam,
                        title: form.title,
                        subtitle: form.subtitle,
                        content: form.content,
                        ...(form.imageUrl !== currentBlog?.imageUrl
                            ? { imageUrl: form.imageUrl }
                            : {}),
                    })
                );
                if (updateBlogThunk.fulfilled.match(result)) {
                    navigate('/blogs');
                }
            } else {
                const result = await dispatch(
                    createBlogThunk({
                        _id: form.blogId,
                        title: form.title,
                        subtitle: form.subtitle,
                        content: form.content,
                        imageUrl: form.imageUrl,
                    })
                );
                if (createBlogThunk.fulfilled.match(result)) {
                    navigate('/blogs');
                }
            }
        },
        [validate, isEditMode, blogIdParam, form, currentBlog, dispatch, navigate]
    );

    const handleCancel = useCallback(() => {
        navigate('/blogs');
    }, [navigate]);

    // ─── 5. Effects ──────────────────────────────────────────────────────────
    useEffect(() => {
        if (isEditMode && blogIdParam) {
            dispatch(fetchBlogDetails(blogIdParam));
        }
        return () => {
            dispatch(clearCurrentBlog());
        };
    }, [isEditMode, blogIdParam, dispatch]);

    useEffect(() => {
        if (currentBlog && isEditMode) {
            setForm({
                blogId: currentBlog._id,
                title: currentBlog.title,
                subtitle: currentBlog.subtitle,
                content: currentBlog.content ?? '',
                imageUrl: currentBlog.imageUrl,
            });
        }
    }, [currentBlog, isEditMode]);

    // ─── 6. JSX ──────────────────────────────────────────────────────────────
    if (isEditMode && loading === 'loading') {
        return (
            <div className="flex items-center justify-center py-24">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6e64f0]" />
            </div>
        );
    }

    return (
        <div className="w-full max-w-5xl mx-auto flex flex-col min-h-[calc(100vh-8rem)]">
            {/* Scrollable content */}
            <div className="flex-1 space-y-6 pb-28">
                {/* Header */}
                <div>
                    <h2 className="text-3xl font-bold text-[#1e293b]">
                        {isEditMode ? 'Edit Blog' : 'Create Blog'}
                    </h2>
                    <p className="text-base text-[#64748b] mt-1">
                        {isEditMode
                            ? 'Update your blog post details.'
                            : 'Fill in the details to publish a new blog post.'}
                    </p>
                </div>

                {/* API Error */}
                {apiError && (
                    <div className="bg-[#fee2e2] border border-[#ef4444] text-[#ef4444] px-4 py-3 rounded-lg text-sm font-medium">
                        {apiError}
                    </div>
                )}

                <form id="blog-form" onSubmit={handleSubmit} className="space-y-6">
                    {/* Cover Image */}
                    <div className="bg-white rounded-2xl border border-[#e2e8f0] p-6 space-y-3">
                        <label className="block text-sm font-semibold text-[#1e293b]">
                            Cover Image <span className="text-[#ef4444]">*</span>
                        </label>
                        <BlogImageUploader
                            onUploadComplete={handleImageUploadComplete}
                            currentImageUrl={form.imageUrl || undefined}
                            disabled={saving}
                        />
                        {validationErrors.image && (
                            <p className="text-sm text-[#ef4444]">{validationErrors.image}</p>
                        )}
                    </div>

                    {/* Title & Subtitle */}
                    <div className="bg-white rounded-2xl border border-[#e2e8f0] p-6 space-y-5">
                        {/* Title */}
                        <div className="space-y-1.5">
                            <label
                                htmlFor="blog-title"
                                className="block text-sm font-semibold text-[#1e293b]"
                            >
                                Title <span className="text-[#ef4444]">*</span>
                            </label>
                            <input
                                id="blog-title"
                                type="text"
                                value={form.title}
                                onChange={handleTitleChange}
                                disabled={saving}
                                placeholder="Enter blog title"
                                className={`w-full px-4 py-2.5 border rounded-lg text-[#1e293b] placeholder:text-[#94a3b8] text-base transition-colors focus:outline-none focus:ring-2 focus:ring-[#6e64f0]/30 focus:border-[#6e64f0] ${
                                    validationErrors.title
                                        ? 'border-[#ef4444]'
                                        : 'border-[#e2e8f0]'
                                } disabled:opacity-60 disabled:cursor-not-allowed`}
                            />
                            {validationErrors.title && (
                                <p className="text-sm text-[#ef4444]">{validationErrors.title}</p>
                            )}
                        </div>

                        {/* Subtitle */}
                        <div className="space-y-1.5">
                            <label
                                htmlFor="blog-subtitle"
                                className="block text-sm font-semibold text-[#1e293b]"
                            >
                                Subtitle <span className="text-[#ef4444]">*</span>
                            </label>
                            <textarea
                                id="blog-subtitle"
                                value={form.subtitle}
                                onChange={handleSubtitleChange}
                                disabled={saving}
                                placeholder="Enter a short description of the blog"
                                rows={3}
                                className={`w-full px-4 py-2.5 border rounded-lg text-[#1e293b] placeholder:text-[#94a3b8] text-base transition-colors focus:outline-none focus:ring-2 focus:ring-[#6e64f0]/30 focus:border-[#6e64f0] resize-none ${
                                    validationErrors.subtitle
                                        ? 'border-[#ef4444]'
                                        : 'border-[#e2e8f0]'
                                } disabled:opacity-60 disabled:cursor-not-allowed`}
                            />
                            {validationErrors.subtitle && (
                                <p className="text-sm text-[#ef4444]">{validationErrors.subtitle}</p>
                            )}
                        </div>
                    </div>

                    {/* Content Editor */}
                    <div className="bg-white rounded-2xl border border-[#e2e8f0] p-6 space-y-3">
                        <label className="block text-sm font-semibold text-[#1e293b]">
                            Content <span className="text-[#ef4444]">*</span>
                        </label>
                        <BlogContentEditor
                            content={form.content}
                            onChange={handleContentChange}
                            disabled={saving}
                        />
                        {validationErrors.content && (
                            <p className="text-sm text-[#ef4444]">{validationErrors.content}</p>
                        )}
                    </div>
                </form>
            </div>

            {/* Sticky footer */}
            <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#e2e8f0] shadow-lg">
                <div className="w-full max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
                    <p className="text-sm text-[#64748b] hidden sm:block">
                        {isEditMode
                            ? 'Changes will update the published blog post.'
                            : 'Blog will be published immediately after creation.'}
                    </p>
                    <div className="flex items-center gap-3 ml-auto">
                        <button
                            type="button"
                            onClick={handleCancel}
                            disabled={saving}
                            className="px-6 py-2.5 border border-[#e2e8f0] rounded-lg text-[#64748b] hover:bg-[#f8fafc] text-sm font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            form="blog-form"
                            disabled={saving || !isFormValid}
                            className="px-6 py-2.5 bg-gradient-to-r from-[#6e64f0] to-[#8b7cf6] text-white rounded-lg font-bold text-sm hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {saving && (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            )}
                            {saving
                                ? 'Saving...'
                                : isEditMode
                                  ? 'Update Blog'
                                  : 'Publish Blog'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
